<template>
  <v-row class="ma-3 justify-center">
    <v-card
      v-if="!organization"
      class="px-3 col-12 elevation-0"
    >
      <v-card-text class="error-message pa-3">
        <v-row>
          <v-col>
            <v-icon>error_outline</v-icon>

            <span class="header-2 pl-2">
              Not Found
            </span>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card
      v-else-if="isOurCompany"
      class="px-3 col-12 elevation-0"
    >
      <v-card-title>
        <v-col class="col-2">
          <v-avatar
            class="elevation-1"
          >
            <img
              :src="icons.organization"
              alt="Avatar"
              color="offwhite"
              size="48"
            >
          </v-avatar>
        </v-col>

        <v-col>
          <v-row class="col-12 header-2">
            OAO
          </v-row>
        </v-col>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col class="title col-2">
            Profile
          </v-col>

          <v-col class="pl-2">
            <v-col class="py-2">
              <div class="font-weight-bold">
                Headquarters
              </div>

              <div>
                451 Broadway, 3rd Floor<br>
                New York, NY 10013
              </div>
            </v-col>

            <v-col class="py-2">
              <div class="font-weight-bold">
                California
              </div>

              <div>
                22 Battery Street, #700<br>
                San Francisco, CA 94111
              </div>
            </v-col>

            <v-col class="py-2">
              <div class="font-weight-bold">
                Colorado
              </div>

              <div>
                8700 Turnpike Drive, Suite 495<br>
                Westminster, CO 80031
              </div>
            </v-col>

            <v-col class="py-2">
              <div class="font-weight-bold">
                Illinois
              </div>

              <div>
                350 North LaSalle Blvd, Suite 420<br>
                Chicago, IL 60654
              </div>
            </v-col>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="title col-2">
            Metadata
          </v-col>

          <v-col class="pl-2">
            <v-col>
              Formed in

              <span class="font-weight-bold">
                2001
              </span>

              by

              <span class="font-weight-bold">
                Craig Leshen
              </span>
            </v-col>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <template v-else>
      <v-col class="col-9">
        <v-card
          class="px-3 pt-3 pb-6 elevation-0"
        >
          <v-card-title class="darkblue--text">
            {{ organization.orgName || 'Organization Name Unknown' }}
          </v-card-title>

          <v-card-text>
            <v-row>
              <v-col
                v-if="isInternalAdminUser"
              >
                <v-row class="subtitle-2">
                  <v-col>
                    <v-tooltip left>
                      <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                        <div :class="`cursor-pointer ${titleColor}--text`">
                          <span
                            v-bind="tooltipAttrs"
                            v-on="tooltipOn"
                          >
                            Client ID
                          </span>
                          <span
                            class="toasted--text"
                          >
                            *
                          </span>
                        </div>
                      </template>

                      <span>Admin Only</span>
                    </v-tooltip>
                  </v-col>
                </v-row>

                <v-row class="body-2 mt-0">
                  <v-col
                    :class="`py-0 font-weight-medium ${textColor}--text`"
                  >
                    {{ organization.clientId || 'None' }}
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-row class="subtitle-2">
                  <v-col :class="`${titleColor}--text`">
                    Publisher ID
                  </v-col>
                </v-row>
                <v-row class="body-2 mt-0">
                  <v-col :class="`py-0 font-weight-medium ${textColor}--text`">
                    {{ organization.publisherId || 'None' }}
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-row class="subtitle-2">
                  <v-col :class="`${titleColor}--text`">
                    Billing Code
                  </v-col>
                </v-row>
                <v-row class="body-2 mt-0">
                  <v-col :class="`py-0 font-weight-medium ${textColor}--text`">
                    {{ organization.billingCode || 'None' }}
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-row class="subtitle-2">
                  <v-col :class="`${titleColor}--text`">
                    Network Code
                  </v-col>
                </v-row>
                <v-row class="body-2 mt-0">
                  <v-col
                    v-if="adServerUrl"
                    :class="`py-0 font-weight-medium ${textColor}--text`"
                  >
                    <a
                      :href="adServerUrl"
                      target="_blank"
                    >
                      {{ organization.networkCode }}
                    </a>
                  </v-col>

                  <v-col
                    v-else
                    :class="`py-0 font-weight-medium ${textColor}--text`"
                  >
                    None
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card
          class="px-3 pt-3 pb-6 elevation-0"
        >
          <v-card-subtitle :class="`font-weight-bold ${titleColor}--text text-uppercase`">
            Service Data
          </v-card-subtitle>

          <v-card-text>
            <v-row>
              <v-col>
                <v-row class="subtitle-2">
                  <v-col :class="`${titleColor}--text`">
                    Status
                  </v-col>
                </v-row>
                <v-row class="body-2 mt-0">
                  <v-col
                    :class="`py-0 font-weight-medium ${textColor}--text`"
                  >
                    {{ isActiveLabel }}
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-row class="subtitle-2">
                  <v-col :class="`${titleColor}--text`">
                    Internal Use Only
                  </v-col>
                </v-row>
                <v-row class="body-2 mt-0">
                  <v-col
                    :class="`py-0 font-weight-medium ${textColor}--text`"
                  >
                    {{ isInternalUseOnly }}
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-row class="subtitle-2">
                  <v-col :class="`${titleColor}--text`">
                    Timesheet-Enabled
                  </v-col>
                </v-row>
                <v-row class="body-2 mt-0">
                  <v-col
                    :class="`py-0 font-weight-medium ${textColor}--text`"
                  >
                    {{ hasTimesheetReport }}
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-row class="subtitle-2">
                  <v-col :class="`${titleColor}--text`">
                    Reports
                  </v-col>
                </v-row>

                <v-row
                  class="pl-1 body-2"
                >
                  <v-col class="py-0 font-weight-medium">
                    <v-icon
                      class="pr-2"
                      :color="hasPacingReport.color"
                      :title="hasPacingReport.value"
                      small
                    >
                      {{ hasPacingReport.icon }}
                    </v-icon>

                    <span :class="`outline ${textColor}--text`">Pacing</span>
                  </v-col>
                </v-row>

                <v-row
                  class="pl-1 body-2"
                >
                  <v-col class="py-0 font-weight-medium">
                    <v-icon
                      class="pr-2"
                      :color="hasDiscrepancyReport.color"
                      :title="hasDiscrepancyReport.value"
                      small
                    >
                      {{ hasDiscrepancyReport.icon }}
                    </v-icon>

                    <span :class="`outline ${textColor}--text`">Discrepancy</span>
                  </v-col>
                </v-row>

                <v-row
                  class="pl-1 body-2"
                >
                  <v-col class="py-0 font-weight-medium">
                    <v-icon
                      class="pr-2"
                      :color="hasHealthAdxReport.color"
                      :title="hasHealthAdxReport.value"
                      small
                    >
                      {{ hasHealthAdxReport.icon }}
                    </v-icon>

                    <span :class="`outline ${textColor}--text`">AdX Health</span>
                  </v-col>
                </v-row>

                <v-row
                  class="pl-1 body-2"
                >
                  <v-col class="py-0 font-weight-medium">
                    <v-icon
                      class="pr-2"
                      :color="hasHealthNetworkReport.color"
                      :title="hasHealthNetworkReport.value"
                      small
                    >
                      {{ hasHealthNetworkReport.icon }}
                    </v-icon>

                    <span :class="`outline ${textColor}--text`">Network Health</span>
                  </v-col>
                </v-row>

                <v-row
                  class="pl-1 body-2"
                >
                  <v-col class="py-0 font-weight-medium">
                    <v-icon
                      class="pr-2"
                      :color="hasYieldPartnerReport.color"
                      :title="hasYieldPartnerReport.value"
                      small
                    >
                      {{ hasYieldPartnerReport.icon }}
                    </v-icon>

                    <span :class="`outline ${textColor}--text`">AdX & OB</span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row class="mt-6">
              <v-col>
                <v-row class="subtitle-2">
                  <v-col :class="`${titleColor}--text`">
                    Teams
                  </v-col>
                </v-row>

                <v-row
                  v-if="teams"
                  class="body-2 mt-0"
                >
                  <v-col>
                    <v-data-table
                      :headers="teamColumns"
                      :items="teams"
                      dense
                      disable-pagination
                      hide-default-footer
                    />
                  </v-col>
                </v-row>

                <v-row
                  v-else
                  class="body-2"
                >
                  <v-col class="py-0 font-weight-medium ${titleColor}--text">
                    False
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card
          class="px-3 elevation-0"
        >
          <v-card-subtitle :class="`font-weight-bold ${titleColor}--text py-0`">
            <v-row class="align-center justify-space-between">
              <v-col class="col-auto text-uppercase">
                Persons
              </v-col>

              <v-col class="col-auto pr-0">
                <v-text-field
                  v-model="searchPersons"
                  dense
                  append-icon="search"
                  label="Search"
                  single-line
                  hide-details
                  x-small
                  class="pt-0 mt-0"
                />
              </v-col>
            </v-row>
          </v-card-subtitle>

          <v-row>
            <v-col class="px-0 pb-0">
              <v-divider />
            </v-col>
          </v-row>

          <v-card-text class="px-0 pb-0">
            <v-data-table
              :headers="userHeaders"
              :items="users"
              :items-per-page="5"
              class="elevation-0"
              :search="searchPersons"
            >
              <template v-slot:item.access.active="{ item }">
                <v-icon
                  :color="(item.access.active && 'offblue') || 'coolgray'"
                  :readonly="true"
                >
                  {{ (item.access.active && 'check') || 'remove' }}
                </v-icon>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <v-card
          class="px-3 pt-3 pb-6 elevation-0"
        >
          <v-card-subtitle class="font-weight-bold midgray--text text-uppercase">
            Change Log
          </v-card-subtitle>

          <v-card-text>
            <v-row>
              <v-col>
                <v-row class="body-2">
                  <v-col class="py-0 font-weight-medium">
                    <span class="pr-1 midgray--text">Created</span>

                    <span :class="`${textColor}--text`">
                      {{ createdOn }}
                    </span>

                    <span :class="`px-1 ${titleColor}--text`">by</span>

                    <span :class="`${textColor}--text`">
                      {{ createdBy }}
                    </span>
                  </v-col>
                </v-row>

                <v-row
                  v-if="updatedBy"
                  class="body-2"
                >
                  <v-col class="py-0 font-weight-medium">
                    <span class="pr-1 midgray--text">Updated</span>

                    <span :class="`${textColor}--text`">
                      {{ updatedOn }}
                    </span>

                    <span :class="`px-1 ${titleColor}--text`">by</span>

                    <span :class="`${textColor}--text`">
                      {{ updatedBy }}
                    </span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </template>
  </v-row>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import dates from 'src/utils/dates'
import gam from 'src/utils/gam'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapState: mapLayoutState, mapGetters: mapLayoutGetters } = createNamespacedHelpers('layout')
const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'OrgProfile',
  data() {
    return {
      searchPersons: '',
      teamColumns: [
        {
          sortable: true,
          text: 'Team Name',
          value: 'teamName',
        },
        {
          sortable: true,
          text: 'Team ID',
          value: 'teamId',
        },
        {
          sortable: true,
          text: 'Team Code',
          value: 'teamCode',
        },
      ],
    }
  },
  computed: {
    ...mapAuthGetters([
      'isInternalUser',
      'isInternalAdminUser',
    ]),
    ...mapLayoutGetters([
      'textColor',
      'titleColor',
    ]),
    ...mapLayoutState({
      icons: (state) => state.icons,
      valueIcons: (state) => state.icons.values,
    }),
    ...mapToolsGetters({
      persons: 'persons',
      organization: 'selectedSearch',
    }),
    ...mapToolsState({
      associationId: (state) => state.associationId,
    }),
    isOurCompany() {
      const { associationId } = this
      return ['0', 'oao'].includes(associationId)
    },

    adServerUrl() {
      const { organization = {} } = this
      const { networkCode } = organization

      if (!networkCode) return false

      return gam.network(networkCode)
    },
    isDeleted() {
      const { organization = {} } = this
      const { isDeleted } = organization

      return isDeleted
    },
    isActive() {
      const { organization = {} } = this
      const { isActive } = organization

      return isActive
    },
    isActiveColor() {
      const { isActive, valueIcons } = this
      const status = String(isActive)
      const { color = 'blueblack' } = valueIcons[status] || {}

      return color
    },
    isActiveLabel() {
      if (this.isDeleted) return 'Deleted'
      return this.isActive ? 'Active' : 'Inactive'
    },
    hasPacingReport() {
      const { organization = {}, valueIcons } = this
      const { hasPacing: value, hasPacingReport = false } = organization
      const propertyName = String(hasPacingReport).toLowerCase()

      const styles = valueIcons[propertyName] || {}

      return { ...styles, value }
    },
    hasDiscrepancyReport() {
      const { organization = {}, valueIcons } = this
      const { hasDiscrepancy: booleanValue, hasDiscrepancyReport = false } = organization
      const propertyName = String(hasDiscrepancyReport).toLowerCase()

      const styles = valueIcons[propertyName] || {}

      return { ...styles, value: booleanValue }
    },
    hasHealthAdxReport() {
      const { organization = {}, valueIcons } = this
      const { hasHealthAdx: value, hasHealthAdxReport = false } = organization
      const propertyName = String(hasHealthAdxReport).toLowerCase()

      const styles = valueIcons[propertyName] || {}

      return { ...styles, value }
    },
    hasHealthNetworkReport() {
      const { organization = {}, valueIcons } = this
      const { hasHealthNetwork: value, hasHealthNetworkReport = false } = organization
      const propertyName = String(hasHealthNetworkReport).toLowerCase()

      const styles = valueIcons[propertyName] || {}

      return { ...styles, value }
    },
    hasYieldPartnerReport() {
      const { organization = {}, valueIcons } = this
      const { hasProgrammatic: value, hasYieldPartnerReport = false } = organization
      const propertyName = String(hasYieldPartnerReport).toLowerCase()

      const styles = valueIcons[propertyName] || {}

      return { ...styles, value }
    },
    hasTimesheetReport() {
      const { organization = {} } = this
      const { hasTimesheetReport } = organization

      return hasTimesheetReport
    },
    isInternalUseOnly() {
      const { organization = {} } = this
      const { isInternalUseOnly } = organization

      return isInternalUseOnly
    },
    teams() {
      const { organization = {} } = this
      const { teams = [] } = organization

      return teams
    },
    createdBy() {
      const { organization = {} } = this
      const unknownActor = 'Unknown'
      const { assoc_email = unknownActor, updated_dt } = organization

      return !updated_dt ? assoc_email : unknownActor
    },
    createdOn() {
      const { organization = {} } = this
      const { created_dt } = organization

      return dates.getFormattedLocalDateTime(created_dt)
    },
    updatedBy() {
      const { organization = {} } = this
      const { assoc_email, updated_dt } = organization

      return updated_dt ? assoc_email : null
    },
    updatedOn() {
      const { organization = {} } = this
      const { updated_dt } = organization

      if (!updated_dt) return 'Date Unknown'

      return dates.getFormattedLocalDateTime(updated_dt)
    },

    users() {
      const { personAssignments, persons, organization } = this
      const { billingCode } = organization
      const { assignments } = personAssignments || {}
      const havePersonAssignments = assignments && assignments.length > 0

      return persons.filter((person) => person.billingCodes.indexOf(billingCode) !== -1).map((person) => {
        const foundAssignment = havePersonAssignments && assignments.find((assignment) => assignment.personId === person.personId)

        if (foundAssignment && foundAssignment.activeRoleAssignment) {
          person.assignment = foundAssignment.roleName
        }

        return person
      })
    },
    userHeaders() {
      return [
        {
          text: 'Name',
          value: 'personName',
        },
        {
          text: 'Email',
          value: 'email',
        },
        {
          text: 'Active',
          value: 'access.active',
        },
        {
          text: 'Access Role',
          value: 'access.role',
        },
        {
          text: 'Assignment',
          value: 'assignment',
        },
      ]
    },

  },
}
</script>

<style lang="scss" scoped>
.v-card__subtitle {
  letter-spacing: 0.025rem;
}
</style>
